import { getAuthHeaders } from "../../../utils/authHeaders";
import { bullionOddsSnapshotMarkets } from "./bullionOddsSnapshot";

export type BullionFixtureFrozenOddsResponse = {
    success?: boolean;
    ready?: boolean;
    opening_odds?: unknown;
    markets?: unknown[];
};

const frozenOddsInflight = new Map<string, Promise<BullionFixtureFrozenOddsResponse>>();

/**
 * Load `bullion_match.opening_odds` for one fixture (board hydration when lobby cache / Trader rows lack frozen lines).
 */
export async function fetchBullionFixtureFrozenOdds(matchId: string): Promise<BullionFixtureFrozenOddsResponse> {
    const want = String(matchId || "").trim();
    if (!want) return { success: false, ready: false, markets: [] };

    const existing = frozenOddsInflight.get(want);
    if (existing) return existing;

    const base = (process.env.API_URL || "").replace(/\/$/, "");
    const url = `${base}/bullion/fixture-frozen-odds?matchId=${encodeURIComponent(want)}`;

    const promise = (async () => {
        try {
            const res = await fetch(url, { method: "GET", headers: getAuthHeaders() });
            const data = (await res.json().catch(() => ({}))) as BullionFixtureFrozenOddsResponse;
            if (!res.ok) return { success: false, ready: false, markets: [] };
            const markets = Array.isArray(data.markets)
                ? data.markets
                : bullionOddsSnapshotMarkets(data.opening_odds);
            return {
                ...data,
                markets,
                ready: Boolean(data.ready) || markets.length > 0,
            };
        } catch {
            return { success: false, ready: false, markets: [] };
        } finally {
            frozenOddsInflight.delete(want);
        }
    })();

    frozenOddsInflight.set(want, promise);
    return promise;
}

/** Merge frozen `opening_odds` / `markets` onto a lobby raw row when missing. */
export function mergeBullionFrozenOddsOntoLobbyRow(
    row: unknown,
    frozen: { opening_odds?: unknown; markets?: unknown[] } | null | undefined
): unknown {
    if (!row || typeof row !== "object" || !frozen) return row;
    const markets = Array.isArray(frozen.markets) ? frozen.markets : bullionOddsSnapshotMarkets(frozen.opening_odds);
    if (markets.length === 0) return row;
    const r = row as Record<string, unknown>;
    const hasOpening = bullionOddsSnapshotMarkets(r.opening_odds ?? r.openingOdds).length > 0;
    const hasMarkets = bullionOddsSnapshotMarkets(r.markets).length > 0;
    if (hasOpening && hasMarkets) return row;
    const opening = frozen.opening_odds ?? r.opening_odds ?? r.openingOdds ?? { markets };
    return {
        ...r,
        opening_odds: hasOpening ? r.opening_odds ?? r.openingOdds : opening,
        markets: hasMarkets ? r.markets : markets,
        sport: r.sport || "cricket",
    };
}
